import React, { useEffect, useMemo, useState } from "react";
import { adminApi, API } from "@/lib/lnlApi";
import { toast } from "sonner";
import {
    Check, X as XIcon, Eye, Archive, Trash2, Pencil, Search as SearchIcon,
    Image as ImageIcon, Save, ArrowUp, ArrowDown, ShieldCheck,
} from "lucide-react";

function fmtErr(e) {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    return d?.msg || e?.message || "Something went wrong.";
}

const STATUS_TABS = [
    { key: "PENDING", label: "Pending" },
    { key: "NEEDS_CORRECTION", label: "Needs correction" },
    { key: "APPROVED", label: "Approved" },
    { key: "REJECTED", label: "Rejected" },
    { key: "ARCHIVED", label: "Archived" },
];

const STATUS_TONE = {
    PENDING: "bg-amber-100 text-amber-900 border-amber-200",
    NEEDS_CORRECTION: "bg-orange-100 text-orange-900 border-orange-200",
    APPROVED: "bg-emerald-100 text-emerald-900 border-emerald-200",
    REJECTED: "bg-rose-100 text-rose-900 border-rose-200",
    ARCHIVED: "bg-slate-200 text-slate-800 border-slate-300",
};

export default function CampusSubmissionsPanel() {
    const [tab, setTab] = useState("PENDING");
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState({ pending: 0, approved: 0, rejected: 0, archived: 0 });
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const [{ data: rows }, { data: sum }] = await Promise.all([
                adminApi.get("/cms/campus-submissions", { params: { status: tab, q: q || undefined } }),
                adminApi.get("/cms/campus-submissions/summary"),
            ]);
            setItems(rows || []);
            setSummary(sum || {});
        } catch (e) {
            toast.error(fmtErr(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

    async function approve(id, publish = true) {
        try {
            await adminApi.post(`/cms/campus-submissions/${id}/approve`, { publish, notify_visitor: true });
            toast.success(publish ? "Approved & published" : "Approved (kept hidden)");
            load();
            if (selected?.id === id) setSelected(null);
        } catch (e) { toast.error(fmtErr(e)); }
    }
    async function reject(id, reason) {
        try {
            await adminApi.post(`/cms/campus-submissions/${id}/reject`, { reason, notify_visitor: false });
            toast.success("Rejected");
            load();
            if (selected?.id === id) setSelected(null);
        } catch (e) { toast.error(fmtErr(e)); }
    }
    async function requestCorrection(id) {
        const note = window.prompt("What needs to be corrected or clarified? This is emailed to the submitter.");
        if (!note || !note.trim()) return;
        try {
            await adminApi.post(`/cms/campus-submissions/${id}/request-correction`, { note, notify_visitor: true });
            toast.success("Correction requested");
            load();
            if (selected?.id === id) setSelected(null);
        } catch (e) { toast.error(fmtErr(e)); }
    }
    async function archive(id) {
        if (!window.confirm("Archive this submission?")) return;
        try { await adminApi.post(`/cms/campus-submissions/${id}/archive`); toast.success("Archived"); load(); }
        catch (e) { toast.error(fmtErr(e)); }
    }
    async function del(id) {
        if (!window.confirm("Permanently delete this submission? This cannot be undone.")) return;
        try { await adminApi.delete(`/cms/campus-submissions/${id}`); toast.success("Deleted"); load(); if (selected?.id === id) setSelected(null); }
        catch (e) { toast.error(fmtErr(e)); }
    }
    async function togglePublish(item) {
        try {
            await adminApi.post(`/cms/campus-submissions/${item.id}/publish`, { is_public: !item.is_public });
            toast.success(!item.is_public ? "Now public" : "Hidden from public");
            load();
        } catch (e) { toast.error(fmtErr(e)); }
    }

    async function reorder(item, dir) {
        const list = items.filter((i) => i.status === "APPROVED").sort((a, b) => (a.order || 999) - (b.order || 999));
        const idx = list.findIndex((i) => i.id === item.id);
        if (idx < 0) return;
        const swapWith = dir === "up" ? idx - 1 : idx + 1;
        if (swapWith < 0 || swapWith >= list.length) return;
        const reordered = [...list];
        [reordered[idx], reordered[swapWith]] = [reordered[swapWith], reordered[idx]];
        const payload = reordered.map((it, i) => ({ id: it.id, order: i }));
        try {
            await adminApi.post("/cms/campus-submissions/reorder", { items: payload });
            load();
        } catch (e) { toast.error(fmtErr(e)); }
    }

    return (
        <div className="space-y-4" data-testid="campus-submissions-panel">
            {/* Summary chips */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {STATUS_TABS.map((t) => {
                    const count = summary[t.key.toLowerCase()] ?? 0;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`lnl-card p-4 text-left transition-shadow ${tab === t.key ? "ring-2 ring-lnl-green shadow-md" : "hover:shadow"}`}
                            data-testid={`campus-tab-${t.key.toLowerCase()}`}
                        >
                            <div className="text-[11px] uppercase tracking-widest text-lnl-gray font-heading font-semibold">{t.label}</div>
                            <div className="font-heading text-2xl font-bold text-lnl-charcoal mt-1">{count}</div>
                        </button>
                    );
                })}
            </div>

            {/* Search + filters */}
            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[240px] max-w-md">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-lnl-gray" />
                    <input
                        type="text" value={q} onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        placeholder="Search by name, email, caption…"
                        className="w-full rounded-md border border-lnl-border pl-9 pr-3 py-2 text-sm"
                        data-testid="campus-search"
                    />
                </div>
                <button onClick={load} className="lnl-btn-secondary text-sm" data-testid="campus-search-btn">Search</button>
            </div>

            {/* Submissions grid */}
            {loading ? (
                <div className="text-sm text-lnl-gray py-8 text-center">Loading submissions…</div>
            ) : items.length === 0 ? (
                <div className="lnl-card p-10 text-center text-lnl-gray">
                    No {tab.toLowerCase()} submissions.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="campus-grid">
                    {items.map((item) => (
                        <SubmissionCard
                            key={item.id}
                            item={item}
                            onView={() => setSelected(item)}
                            onApprove={approve}
                            onReject={reject}
                            onRequestCorrection={requestCorrection}
                            onArchive={archive}
                            onDelete={del}
                            onTogglePublish={togglePublish}
                            onReorder={reorder}
                            canReorder={tab === "APPROVED"}
                        />
                    ))}
                </div>
            )}

            {selected && (
                <SubmissionDetailModal
                    item={selected}
                    onClose={() => setSelected(null)}
                    onSaved={() => { setSelected(null); load(); }}
                    onApprove={approve}
                    onReject={reject}
                    onRequestCorrection={requestCorrection}
                    onArchive={archive}
                    onDelete={del}
                    onTogglePublish={togglePublish}
                />
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Submission Card
// ---------------------------------------------------------------------------
function ConsentBadge({ item }) {
    if ((item.source || "visitor") !== "visitor") return null;
    return item.marketing_consent === true
        ? <span className="text-[10px] font-heading font-bold rounded-full px-2 py-0.5 border bg-emerald-50 text-emerald-800 border-emerald-200" data-testid={`consent-yes-${item.id}`}>Marketing consent ✓</span>
        : <span className="text-[10px] font-heading font-bold rounded-full px-2 py-0.5 border bg-rose-50 text-rose-800 border-rose-200" data-testid={`consent-no-${item.id}`}>No marketing consent — can't publish</span>;
}

function SubmissionCard({ item, onView, onApprove, onReject, onRequestCorrection, onArchive, onDelete, onTogglePublish, onReorder, canReorder }) {
    const [rejectMode, setRejectMode] = useState(false);
    const [reason, setReason] = useState("");
    return (
        <article className="lnl-card overflow-hidden" data-testid={`campus-card-${item.id}`}>
            <div className="aspect-[4/3] bg-lnl-off-white overflow-hidden relative">
                {item.has_image ? (
                    <img
                        src={`${API}/cms/campus-submissions/${item.id}/image`}
                        alt={item.caption || item.display_name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                    />
                ) : (
                    <div className="w-full h-full grid place-items-center">
                        <ImageIcon className="h-10 w-10 text-lnl-gray" />
                    </div>
                )}
                <span className={`absolute top-2 left-2 text-[10px] uppercase tracking-widest font-heading font-bold rounded-full px-2 py-0.5 border ${STATUS_TONE[item.status] || ""}`}>
                    {item.status}
                </span>
                {item.status === "APPROVED" && (
                    <span className={`absolute top-2 right-2 text-[10px] uppercase tracking-widest font-heading font-bold rounded-full px-2 py-0.5 border ${item.is_public ? "bg-emerald-600 text-white border-emerald-700" : "bg-slate-200 text-slate-800 border-slate-300"}`}>
                        {item.is_public ? "PUBLIC" : "HIDDEN"}
                    </span>
                )}
                {item.edited_by_admin && (
                    <span className="absolute bottom-2 left-2 text-[10px] bg-blue-100 text-blue-900 border border-blue-200 rounded-full px-2 py-0.5 font-heading font-bold">
                        Edited by admin
                    </span>
                )}
            </div>
            <div className="p-4 space-y-2">
                <div className="min-w-0">
                    <div className="font-heading font-bold text-lnl-charcoal text-sm truncate">{item.caption || "(no caption)"}</div>
                    <div className="text-xs text-lnl-gray truncate">
                        by <b>{item.submitter_name}</b> · <span className="opacity-80">{item.submitter_email}</span>
                    </div>
                    <div className="text-[11px] text-lnl-gray mt-0.5">{new Date(item.created_at).toLocaleString()}</div>
                    <div className="mt-1"><ConsentBadge item={item} /></div>
                </div>
                {item.story && <p className="text-xs text-lnl-charcoal/85 line-clamp-3">{item.story}</p>}

                {!rejectMode ? (
                    <div className="flex flex-wrap items-center gap-1.5 pt-1">
                        <button onClick={onView} className="text-xs inline-flex items-center gap-1 rounded-md border border-lnl-border px-2 py-1 hover:bg-lnl-off-white" data-testid={`view-${item.id}`}>
                            <Eye className="h-3.5 w-3.5" /> View / Edit
                        </button>
                        {["PENDING", "NEEDS_CORRECTION"].includes(item.status) && (
                            <>
                                <button onClick={() => onApprove(item.id, true)} disabled={(item.source || "visitor") === "visitor" && item.marketing_consent !== true} title={item.marketing_consent !== true ? "No marketing consent — approve hidden instead" : ""} className="text-xs inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white px-2 py-1 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`approve-${item.id}`}>
                                    <Check className="h-3.5 w-3.5" /> Approve & Publish
                                </button>
                                <button onClick={() => onApprove(item.id, false)} className="text-xs inline-flex items-center gap-1 rounded-md border border-emerald-600 text-emerald-700 px-2 py-1 hover:bg-emerald-50" data-testid={`approve-hidden-${item.id}`}>
                                    Approve (hidden)
                                </button>
                                <button onClick={() => setRejectMode(true)} className="text-xs inline-flex items-center gap-1 rounded-md bg-rose-600 text-white px-2 py-1 hover:bg-rose-700" data-testid={`reject-${item.id}`}>
                                    <XIcon className="h-3.5 w-3.5" /> Reject
                                </button>
                                <button onClick={() => onRequestCorrection(item.id)} className="text-xs inline-flex items-center gap-1 rounded-md border border-orange-400 text-orange-800 px-2 py-1 hover:bg-orange-50" data-testid={`request-correction-${item.id}`}>
                                    <Pencil className="h-3.5 w-3.5" /> Request correction
                                </button>
                            </>
                        )}
                        {item.status === "APPROVED" && (
                            <>
                                <button onClick={() => onTogglePublish(item)} className="text-xs inline-flex items-center gap-1 rounded-md border border-lnl-border px-2 py-1 hover:bg-lnl-off-white" data-testid={`toggle-public-${item.id}`}>
                                    <ShieldCheck className="h-3.5 w-3.5" /> {item.is_public ? "Hide from public" : "Make public"}
                                </button>
                                {canReorder && (
                                    <>
                                        <button onClick={() => onReorder(item, "up")} className="text-xs inline-flex items-center gap-1 rounded-md border border-lnl-border px-2 py-1 hover:bg-lnl-off-white" title="Move up"><ArrowUp className="h-3.5 w-3.5" /></button>
                                        <button onClick={() => onReorder(item, "down")} className="text-xs inline-flex items-center gap-1 rounded-md border border-lnl-border px-2 py-1 hover:bg-lnl-off-white" title="Move down"><ArrowDown className="h-3.5 w-3.5" /></button>
                                    </>
                                )}
                            </>
                        )}
                        {item.status !== "ARCHIVED" && (
                            <button onClick={() => onArchive(item.id)} className="text-xs inline-flex items-center gap-1 rounded-md border border-lnl-border px-2 py-1 hover:bg-lnl-off-white" data-testid={`archive-${item.id}`}>
                                <Archive className="h-3.5 w-3.5" /> Archive
                            </button>
                        )}
                        <button onClick={() => onDelete(item.id)} className="text-xs inline-flex items-center gap-1 rounded-md border border-rose-300 text-rose-700 px-2 py-1 hover:bg-rose-50" data-testid={`delete-${item.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                        </button>
                    </div>
                ) : (
                    <div className="pt-1">
                        <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                            placeholder="Internal reason (optional, not sent to visitor)"
                            className="w-full text-xs rounded-md border border-lnl-border px-2 py-1"
                            data-testid={`reject-reason-${item.id}`} />
                        <div className="flex gap-1.5 mt-1.5">
                            <button onClick={() => { onReject(item.id, reason); setRejectMode(false); setReason(""); }}
                                className="text-xs inline-flex items-center gap-1 rounded-md bg-rose-600 text-white px-2 py-1" data-testid={`reject-confirm-${item.id}`}>
                                Confirm reject
                            </button>
                            <button onClick={() => { setRejectMode(false); setReason(""); }}
                                className="text-xs rounded-md border border-lnl-border px-2 py-1">
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </article>
    );
}

// ---------------------------------------------------------------------------
// Detail / Edit modal
// ---------------------------------------------------------------------------
function SubmissionDetailModal({ item, onClose, onSaved, onApprove, onReject, onRequestCorrection, onArchive, onDelete, onTogglePublish }) {
    const [form, setForm] = useState({
        display_name: item.display_name || item.submitter_name || "",
        affiliation: item.affiliation || "",
        caption: item.caption || "",
        story: item.story || "",
        alt_text: item.alt_text || "",
        grad_year: item.grad_year || "",
    });
    const [saving, setSaving] = useState(false);

    async function save() {
        setSaving(true);
        try {
            await adminApi.patch(`/cms/campus-submissions/${item.id}`, form);
            toast.success("Saved");
            onSaved();
        } catch (e) { toast.error(fmtErr(e)); }
        finally { setSaving(false); }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="detail-modal">
                <div className="sticky top-0 bg-white border-b border-lnl-border px-6 py-3 flex items-center justify-between rounded-t-lg z-10">
                    <div>
                        <div className="lnl-eyebrow">{item.reference} · {item.status}</div>
                        <div className="font-heading font-bold text-lg text-lnl-charcoal">Review submission</div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-md hover:bg-lnl-off-white" data-testid="detail-close"><XIcon className="h-5 w-5" /></button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
                    {/* Left: image + private info */}
                    <div className="space-y-3">
                        <div className="aspect-[4/3] bg-lnl-off-white rounded-md overflow-hidden">
                            {item.has_image ? (
                                <img src={`${API}/cms/campus-submissions/${item.id}/image`} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full grid place-items-center"><ImageIcon className="h-10 w-10 text-lnl-gray" /></div>
                            )}
                        </div>
                        <div className="lnl-card p-3 text-xs bg-lnl-off-white">
                            <div className="lnl-eyebrow mb-2">Private info (not shown publicly)</div>
                            <div><b>Submitter:</b> {item.submitter_name}</div>
                            <div><b>Email:</b> {item.submitter_email}</div>
                            <div><b>IP:</b> {item.submitter_ip}</div>
                            <div><b>Notice acknowledged:</b> {item.notice_ack ? `✓ ${new Date(item.notice_ack_at).toLocaleString()} (${item.notice_version})` : (item.consent_accepted ? "Legacy consent " + new Date(item.consent_at).toLocaleString() : "—")}</div>
                            <div data-testid="detail-marketing-consent"><b>Marketing consent:</b> {item.marketing_consent ? `✓ Given ${new Date(item.marketing_consent_at).toLocaleString()}` : "Not given — cannot be published"}</div>
                            {item.correction_note && <div className="text-orange-800"><b>Correction requested:</b> {item.correction_note}</div>}
                            {(item.moderation_log || []).length > 0 && (
                                <div className="mt-1 pt-1 border-t border-lnl-border" data-testid="detail-moderation-log"><b>Moderation history:</b>
                                    <ul className="mt-1 space-y-0.5">{item.moderation_log.map((m, i) => <li key={i}>{new Date(m.at).toLocaleString()} · {m.by} · {m.status}{m.is_public ? " (public)" : ""}{m.note ? ` — ${m.note}` : ""}</li>)}</ul>
                                </div>
                            )}
                            {item.social_link && <div><b>Social:</b> <a href={item.social_link} target="_blank" rel="noopener noreferrer" className="text-lnl-green-deep underline">{item.social_link}</a></div>}
                            {item.reviewed_by && <div className="mt-1 pt-1 border-t border-lnl-border"><b>Reviewed by:</b> {item.reviewed_by} @ {new Date(item.reviewed_at).toLocaleString()}</div>}
                            {item.rejection_reason && <div className="text-rose-700"><b>Rejection reason:</b> {item.rejection_reason}</div>}
                        </div>
                    </div>

                    {/* Right: editable fields + actions */}
                    <div className="space-y-3">
                        <div className="lnl-eyebrow">Editable content (preview of what will be public)</div>
                        <Field label="Display name" val={form.display_name} onChange={(v) => setForm({ ...form, display_name: v })} testid="edit-name" />
                        <Field label="Affiliation" val={form.affiliation} onChange={(v) => setForm({ ...form, affiliation: v })} testid="edit-affiliation" />
                        <Field label="Grad year" val={form.grad_year} onChange={(v) => setForm({ ...form, grad_year: v })} testid="edit-gradyear" />
                        <Field label="Caption" val={form.caption} onChange={(v) => setForm({ ...form, caption: v })} testid="edit-caption" />
                        <div>
                            <label className="text-xs font-heading font-semibold text-lnl-gray">Story</label>
                            <textarea rows={5} value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })}
                                className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 text-sm" data-testid="edit-story" />
                        </div>
                        <Field label="Alt text (for accessibility)" val={form.alt_text} onChange={(v) => setForm({ ...form, alt_text: v })} testid="edit-alt" />

                        <div className="flex flex-wrap gap-2 pt-2 border-t border-lnl-border">
                            <button onClick={save} disabled={saving} className="lnl-btn-primary text-sm" data-testid="detail-save">
                                <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save edits"}
                            </button>
                            {["PENDING", "NEEDS_CORRECTION"].includes(item.status) && (
                                <>
                                    <button onClick={() => onApprove(item.id, true)} disabled={(item.source || "visitor") === "visitor" && item.marketing_consent !== true} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white px-3 py-2 text-sm hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="detail-approve">
                                        <Check className="h-4 w-4" /> Approve & Publish
                                    </button>
                                    <button onClick={() => onApprove(item.id, false)} className="inline-flex items-center gap-1 rounded-md border border-emerald-600 text-emerald-700 px-3 py-2 text-sm hover:bg-emerald-50" data-testid="detail-approve-hidden">
                                        Approve (keep hidden)
                                    </button>
                                    <button onClick={() => { const reason = window.prompt("Internal rejection reason (optional):") || ""; onReject(item.id, reason); }}
                                        className="inline-flex items-center gap-1 rounded-md bg-rose-600 text-white px-3 py-2 text-sm hover:bg-rose-700" data-testid="detail-reject">
                                        <XIcon className="h-4 w-4" /> Reject
                                    </button>
                                    <button onClick={() => onRequestCorrection(item.id)} className="inline-flex items-center gap-1 rounded-md border border-orange-400 text-orange-800 px-3 py-2 text-sm hover:bg-orange-50" data-testid="detail-request-correction">
                                        <Pencil className="h-4 w-4" /> Request correction
                                    </button>
                                </>
                            )}
                            {item.status === "APPROVED" && (
                                <button onClick={() => onTogglePublish(item)} className="inline-flex items-center gap-1 rounded-md border border-lnl-border px-3 py-2 text-sm hover:bg-lnl-off-white" data-testid="detail-toggle">
                                    <ShieldCheck className="h-4 w-4" /> {item.is_public ? "Hide from public" : "Make public"}
                                </button>
                            )}
                            {item.status !== "ARCHIVED" && (
                                <button onClick={() => onArchive(item.id)} className="inline-flex items-center gap-1 rounded-md border border-lnl-border px-3 py-2 text-sm hover:bg-lnl-off-white" data-testid="detail-archive">
                                    <Archive className="h-4 w-4" /> Archive
                                </button>
                            )}
                            <button onClick={() => onDelete(item.id)} className="inline-flex items-center gap-1 rounded-md border border-rose-300 text-rose-700 px-3 py-2 text-sm hover:bg-rose-50 ml-auto" data-testid="detail-delete">
                                <Trash2 className="h-4 w-4" /> Delete
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, val, onChange, testid }) {
    return (
        <div>
            <label className="text-xs font-heading font-semibold text-lnl-gray">{label}</label>
            <input type="text" value={val} onChange={(e) => onChange(e.target.value)}
                className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 text-sm" data-testid={testid} />
        </div>
    );
}
